// src/utils/generateProjectPdf.js
// ─────────────────────────────────────────────────────────────────────────────
// Generates PDF using jsPDF directly — NO html2canvas, NO screenshots.
// Install: npm install jspdf
// ─────────────────────────────────────────────────────────────────────────────
import { jsPDF } from "jspdf";

const COUNTRY_MAP = { US: "United States", CA: "Canada" };
const STATE_MAP = {
    AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California", CO: "Colorado",
    CT: "Connecticut", DE: "Delaware", DC: "District of Columbia", FL: "Florida", GA: "Georgia",
    GU: "Guam", HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa", KS: "Kansas",
    KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan",
    MN: "Minnesota", MS: "Mississippi", MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada",
    NH: "New Hampshire", NJ: "New Jersey", NM: "New Mexico", NY: "New York", NC: "North Carolina",
    ND: "North Dakota", OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania", PR: "Puerto Rico",
    RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota", TN: "Tennessee", TX: "Texas",
    UT: "Utah", VT: "Vermont", VA: "Virginia", WA: "Washington", WV: "West Virginia", WI: "Wisconsin",
    WY: "Wyoming", BC: "British Columbia", ON: "Ontario", NL: "Newfoundland and Labrador",
    NS: "Nova Scotia", PE: "Prince Edward Island", NB: "New Brunswick", QC: "Quebec", MB: "Manitoba",
    SK: "Saskatchewan", AB: "Alberta", NT: "Northwest Territories", NU: "Nunavut", YT: "Yukon Territory",
};
const PIPE_MIN = { "2": 15, "3": 35, "4": 50, "6": 100 };
const dCountry = (r = "") => COUNTRY_MAP[r?.toUpperCase()] || r || "N/A";
const dState = (r = "") => STATE_MAP[r?.toUpperCase()] || r || "N/A";

// ── sanitize: remove/replace special chars that cause jsPDF letter-spacing bug ──
const san = (s) => {
    if (s == null) return "";
    return String(s)
        .replace(/\u00AE/g, "(R)")   // ® → (R)
        .replace(/\u2019/g, "'")     // ' → '
        .replace(/\u2018/g, "'")     // ' → '
        .replace(/\u201C/g, '"')     // " → "
        .replace(/\u201D/g, '"')     // " → "
        .replace(/\u2013/g, "-")     // – → -
        .replace(/\u2014/g, "--")    // — → --
        .replace(/\u00BD/g, "1/2")   // ½
        .replace(/\u00BC/g, "1/4")   // ¼
        .replace(/\u00BE/g, "3/4")   // ¾
        .replace(/[^\x00-\x7F]/g, ""); // strip remaining non-ASCII
};

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const PW = 210, PH = 297, M = 14, CW = PW - M * 2; // A4, 14mm margins, 182mm content
const BLUE = [62, 108, 175], WHITE = [255, 255, 255], DARK = [25, 25, 25];
const GREY = [105, 105, 105], LG = [230, 230, 230], LBG = [245, 248, 255];

// ─────────────────────────────────────────────────────────────────────────────
// IMAGE HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const toB64 = (url) => new Promise(res => {
    const img = new Image(); img.crossOrigin = "anonymous";
    img.onload = () => {
        try {
            const c = document.createElement("canvas");
            c.width = img.naturalWidth; c.height = img.naturalHeight;
            c.getContext("2d").drawImage(img, 0, 0);
            res(c.toDataURL("image/png"));
        } catch { res(null); }
    };
    img.onerror = () => res(null); img.src = url;
});

const gatherImages = async (pd, sd) => {
    const mp = {};
    const add = fn => { if (fn) { mp[`${process.env.REACT_APP_IMAGE_BASEURL}${fn}`] = null; } };
    add(pd?.images?.[0]?.fileName);
    add(sd?.images?.[0]?.fileName);
    (pd?.relatedProducts || []).forEach(r => add(r?.images?.[0]?.fileName));
    const logoUrl = `${window.location.origin}/images/built-img.png`;
    mp[logoUrl] = null;
    await Promise.all(Object.keys(mp).map(async u => { mp[u] = await toB64(u); }));
    return { mp, logoUrl };
};

// ─────────────────────────────────────────────────────────────────────────────
// PDF CLASS
// ─────────────────────────────────────────────────────────────────────────────
class P {
    constructor() {
        this.doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
        this.y = M;
    }

    /* ── pagination ── */
    pg() { this.doc.addPage(); this.y = M; }
    chk(h) { if (this.y + h > PH - M - 2) this.pg(); }
    gap(mm = 4) { this.y += mm; }

    /* ── font ── */
    f(style = "normal", size = 9, color = DARK) {
        this.doc.setFont("helvetica", style);
        this.doc.setFontSize(size);
        this.doc.setTextColor(...color);
    }

    /* ── text — always sanitized, never overflows right margin ── */
    t(raw, x, y, opts = {}) {
        const s = san(raw);
        if (!s) return;
        const maxW = opts.maxW || (PW - M - x + 0.5); // clamp to page right edge
        if (opts.align === "center") {
            try { this.doc.text(s, x, y, { align: "center" }); } catch (e) { }
            return;
        }
        // clip to maxW
        const safe = this.doc.splitTextToSize(s, maxW)[0] || "";
        try { this.doc.text(safe, x, y); } catch (e) { }
    }

    /* ── wrapped text — returns new y ── */
    wrap(raw, x, y, maxW, lh = 4.5, style = "normal", size = 8.5, color = DARK) {
        const s = san(raw); if (!s) return y;
        this.f(style, size, color);
        this.doc.splitTextToSize(s, maxW).forEach(l => {
            this.chk(lh + 1);
            try { this.doc.text(l, x, y); } catch (e) { }
            y += lh;
        });
        return y;
    }

    /* ── shapes ── */
    fill(x, y, w, h, c) { this.doc.setFillColor(...c); this.doc.rect(x, y, w, h, "F"); }
    stroke(x, y, w, h, c, lw = 0.25) { this.doc.setDrawColor(...c); this.doc.setLineWidth(lw); this.doc.rect(x, y, w, h, "S"); }
    line(x1, y1, x2, y2, c = LG, lw = 0.25) { this.doc.setDrawColor(...c); this.doc.setLineWidth(lw); this.doc.line(x1, y1, x2, y2); }
    addImg(b, x, y, w, h) { if (!b) return; try { this.doc.addImage(b, "PNG", x, y, w, h); } catch (e) { } }

    /* ── section header ── */
    secH(title) {
        this.chk(11);
        this.fill(M, this.y, CW, 9, BLUE);
        this.f("bold", 10, WHITE);
        this.t(title, M + 4, this.y + 6, { maxW: CW - 8 });
        this.y += 9; this.gap(3);
    }

    /* ── table ── */
    table(heads, rows, ws) {
        const HH = 7.5, RH = 6.5;
        this.chk(HH + RH * 2);
        // header
        this.fill(M, this.y, CW, HH, LG);
        this.f("bold", 7.5, [55, 55, 55]);
        let cx = M + 2;
        heads.forEach((h, i) => { this.t(h, cx, this.y + 5, { maxW: ws[i] - 2 }); cx += ws[i]; });
        this.y += HH;
        // rows
        rows.forEach((row, ri) => {
            this.chk(RH + 1);
            if (ri % 2 === 0) this.fill(M, this.y, CW, RH, [249, 250, 252]);
            this.f("normal", 8, DARK);
            cx = M + 2;
            row.forEach((cell, ci) => {
                this.t(String(cell ?? "-"), cx, this.y + 4.5, { maxW: ws[ci] - 2 });
                cx += ws[ci];
            });
            this.y += RH;
        });
        this.line(M, this.y, M + CW, this.y, LG, 0.3);
    }

    totalRow(cells, ws) {
        this.chk(9); this.gap(1);
        this.line(M, this.y, M + CW, this.y, DARK, 0.6); this.y += 1;
        this.f("bold", 8.5, DARK);
        let cx = M + 2;
        cells.forEach((c, i) => { this.t(String(c ?? "-"), cx, this.y + 5.5, { maxW: ws[i] - 2 }); cx += ws[i]; });
        this.y += 7; this.gap(5);
    }

    /* ── blue 2-col info band ── */
    blueBand(items) {
        if (!items.length) return;
        const cols = 2, cw = CW / cols;
        const rows = Math.ceil(items.length / cols);
        const bh = rows * 13 + 6;
        this.chk(bh);
        const by = this.y;
        this.fill(M, by, CW, bh, BLUE);
        items.forEach((it, i) => {
            const col = i % cols, row = Math.floor(i / cols);
            const ix = M + col * cw + 4, iy = by + 5 + row * 13;
            this.f("bold", 7, [175, 210, 255]); this.t(it.label + ":", ix, iy, { maxW: cw - 6 });
            this.f("normal", 8.5, WHITE);
            const lines = this.doc.splitTextToSize(san(it.value || "N/A"), cw - 6);
            lines.slice(0, 2).forEach((l, li) => { try { this.doc.text(l, ix, iy + 4.5 + li * 4.5); } catch (e) { } });
        });
        this.y = by + bh; this.gap(3);
    }

    /* ── note box ── */
    noteBox(text) {
        const s = san(text); if (!s) return;
        this.f("normal", 7.5, [55, 55, 55]);
        const lines = this.doc.splitTextToSize(s, CW - 9);
        const h = lines.length * 4.5 + 8;
        this.chk(h);
        this.fill(M, this.y, CW, h, LBG);
        this.doc.setFillColor(7, 86, 162); this.doc.rect(M, this.y, 1.8, h, "F");
        lines.forEach((l, i) => { try { this.doc.text(l, M + 5, this.y + 5.5 + i * 4.5); } catch (e) { } });
        this.y += h; this.gap(5);
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// FORMAT HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const fmtGPM = v => v === 0 ? "0.00" : v.toFixed(2);

// ─────────────────────────────────────────────────────────────────────────────
// PRODUCT CARD
// ─────────────────────────────────────────────────────────────────────────────
function drawProductCard(p, product, imgB64) {
    const IW = 34, IH = 34, PAD = 4;
    const tx = M + IW + PAD * 2, tw = CW - IW - PAD * 3 - 2;

    // pre-measure description
    p.f("normal", 7.5, [70, 70, 70]);
    const desc = san(product?.description || "");
    const dLines = desc ? p.doc.splitTextToSize(desc, tw) : [];
    const specCount = 3 + (product?.manufacturer3Id_data?.[0]?.title && product.manufacturer3Id_data[0].title !== "N/A" ? 1 : 0)
        + (product?.drainageSize ? 1 : 0);
    const specH = specCount * 5.5;
    const descH = dLines.length * 4;
    const cardH = Math.max(IH + PAD * 2, specH + descH + 26);

    p.chk(cardH + 6);
    const cy = p.y;

    // card bg + border
    p.fill(M, cy, CW, cardH, [252, 252, 254]);
    p.stroke(M, cy, CW, cardH, LG, 0.3);

    // image
    if (imgB64) p.addImg(imgB64, M + PAD, cy + PAD, IW, IH);
    else p.fill(M + PAD, cy + PAD, IW, IH, [232, 232, 232]);

    // name
    let ty = cy + 7;
    p.f("bold", 11, DARK);
    const nameLines = p.doc.splitTextToSize(san(product?.name || ""), tw);
    nameLines.slice(0, 2).forEach(l => { try { p.doc.text(l, tx, ty); } catch (e) { } ty += 5.5; });
    ty += 2;

    // specs
    const modelNum = san(product?.images?.[0]?.modelName || "N/A");
    const specs = [
        ["Model Number", modelNum],
        ["Flow Rate", `${san(product?.scheduleTypeId_data?.[0]?.title || "N/A")} GPM`],
        ...(product?.manufacturer3Id_data?.[0]?.title && product.manufacturer3Id_data[0].title !== "N/A"
            ? [["Grease Capacity", `${san(product.manufacturer3Id_data[0].title)} lbs`]] : []),
        ["Product Line", san(product?.productLine || "N/A")],
        ...(product?.drainageSize ? [["Drainage Size", `${san(product.drainageSize)}"`]] : []),
    ];
    specs.forEach(([lbl, val]) => {
        p.f("bold", 7.5, GREY); p.t(lbl + ":", tx, ty, { maxW: 34 });
        p.f("normal", 8.5, DARK); p.t(val, tx + 34, ty, { maxW: tw - 34 });
        ty += 5.5;
    });

    // description
    if (dLines.length) {
        ty += 2;
        p.f("bold", 7.5, GREY); p.t("Description:", tx, ty, { maxW: tw }); ty += 4.5;
        dLines.forEach(l => { p.f("normal", 7.5, [70, 70, 70]); try { p.doc.text(l, tx, ty); } catch (e) { } ty += 4; });
    }

    p.y = cy + cardH + 4; p.gap(2);
}

// ─────────────────────────────────────────────────────────────────────────────
// RELATED PRODUCTS  — 3 per row, tall cards, no overflow
// ─────────────────────────────────────────────────────────────────────────────
function drawRelated(p, related, getSrc) {
    const COLS = 3, GAP = 4;
    const CRD_W = (CW - GAP * (COLS - 1)) / COLS;  // ~58mm each
    const IMG_H = 52, NAME_H = 28;           // tall image + generous text area
    const CRD_H = IMG_H + NAME_H;            // ~80mm total

    for (let rs = 0; rs < related.length; rs += COLS) {
        const row = related.slice(rs, rs + COLS);
        p.chk(CRD_H + 8);
        const ry = p.y;

        row.forEach((rp, col) => {
            const cx = M + col * (CRD_W + GAP);
            // white bg + border
            p.fill(cx, ry, CRD_W, CRD_H, WHITE);
            p.stroke(cx, ry, CRD_W, CRD_H, LG, 0.3);
            // image area
            p.fill(cx, ry, CRD_W, IMG_H, [247, 248, 250]);
            const im = getSrc(rp?.images?.[0]?.fileName);
            if (im) p.addImg(im, cx + 3, ry + 3, CRD_W - 6, IMG_H - 6);
            // divider line
            p.line(cx, ry + IMG_H, cx + CRD_W, ry + IMG_H, LG, 0.25);
            // name — max 2 lines, bold
            const nm = san(rp?.name || "");
            p.f("bold", 7.5, DARK);
            const nl = p.doc.splitTextToSize(nm, CRD_W - 6);
            nl.slice(0, 2).forEach((l, li) => { try { p.doc.text(l, cx + 3, ry + IMG_H + 8 + li * 6); } catch (e) { } });
            // model
            const mdl = rp?.images?.[0]?.modelName;
            if (mdl) {
                p.f("normal", 6.5, GREY);
                p.t(`Model: ${san(mdl)}`, cx + 3, ry + IMG_H + 8 + 2 * 6 + 3, { maxW: CRD_W - 6 });
            }
        });

        p.y = ry + CRD_H + GAP + 1;
    }
    p.gap(5);
}

// ─────────────────────────────────────────────────────────────────────────────
// SIZING DETAILS — fully independent two-column layout
// ─────────────────────────────────────────────────────────────────────────────
function drawSizing(p, s1, s2, addr, ctype) {
    const cw = (CW / 2) - 5, lx = M, rx = M + CW / 2 + 3;

    const L = [
        ["Building Description", ctype || "N/A"],
        ["Project Name", s1.projectName || "N/A"],
        ["Address", addr || "N/A"],
        ["Authority Having Jurisdiction", s1.authorityHavingJurisdiction || "N/A"],
        ["Engineer Name", s1.engineerName || s1.representative || "N/A"],
    ];
    const R = [
        ["Location of Interceptor", s2.greeceInterceptorLocation || "N/A"],
        ["Inside Area", s2.insideArea || "N/A"],
        ["Is the Interceptor buried in the ground", s2.willBurried ? "Yes" : "No"],
        ["Install in Vehicle Traffic Area", s2.willInstallVehichleTrafficArea ? "Yes" : "No"],
        ["Install Port", s2.isInstallPort ? "Yes" : "No"],
        ["Interceptor Type", s2.interceptorType
            ? san(s2.interceptorType.replace(/([A-Z])/g, " $1").replace(/^./, c => c.toUpperCase()).trim())
            : "N/A"],
    ];

    // measure total height needed
    const measure = (items) => items.reduce((acc, [, v]) => {
        return acc + 4 + p.doc.splitTextToSize(san(v), cw - 2).length * 4.5 + 4;
    }, 10);
    const totalH = Math.max(measure(L), measure(R));
    p.chk(totalH + 6);

    const sy = p.y;
    p.f("bold", 7.5, GREY);
    p.t("INTERCEPTOR INFORMATION", lx, sy + 4, { maxW: cw });
    p.t("LOCATION DETAILS", rx, sy + 4, { maxW: cw });

    let ly = sy + 11, ry = sy + 11;

    L.forEach(([lbl, val]) => {
        const s = san(val);
        const lines = p.doc.splitTextToSize(s, cw - 2);
        p.f("bold", 7.5, GREY); p.t(lbl + ":", lx, ly, { maxW: cw }); ly += 4;
        p.f("normal", 8.5, DARK);
        lines.forEach(l => { try { p.doc.text(l, lx, ly); } catch (e) { } ly += 4.5; });
        ly += 3;
    });

    R.forEach(([lbl, val]) => {
        const s = san(val);
        const lines = p.doc.splitTextToSize(s, cw - 2);
        p.f("bold", 7.5, GREY); p.t(lbl + ":", rx, ry, { maxW: cw }); ry += 4;
        p.f("normal", 8.5, DARK);
        lines.forEach(l => { try { p.doc.text(l, rx, ry); } catch (e) { } ry += 4.5; });
        ry += 3;
    });

    p.y = Math.max(ly, ry) + 5;
}

// ─────────────────────────────────────────────────────────────────────────────
// REPRESENTATIVE
// ─────────────────────────────────────────────────────────────────────────────
function drawRep(p, rep) {
    const cols = 3, cw = CW / cols;
    const items = [
        ["Name", `${rep.firstName || ""} ${rep.lastName || ""}`.trim()],
        ["Email", rep.email || "N/A"],
        ["Phone", rep.phone || "N/A"],
        ["City", rep.city || "N/A"],
        ["State", dState(rep.state)],
        ["Country", dCountry(rep.country)],
    ];
    const rowH = 12, rows = Math.ceil(items.length / cols);
    p.chk(rows * rowH + 4);
    const sy = p.y;
    items.forEach(([lbl, val], i) => {
        const col = i % cols, row = Math.floor(i / cols);
        const ix = M + col * cw, iy = sy + row * rowH;
        p.f("bold", 7.5, GREY); p.t(lbl + ":", ix, iy + 4, { maxW: cw - 2 });
        p.f("normal", 8.5, DARK); p.t(san(val), ix, iy + 9, { maxW: cw - 2 });
    });
    p.y = sy + rows * rowH + 5; p.gap(2);
}

// ─────────────────────────────────────────────────────────────────────────────
// MODEL BOX
// ─────────────────────────────────────────────────────────────────────────────
function drawModelBox(p, product) {
    const MW = 42, dw = CW - MW - 5;
    const desc = san(product?.description || "N/A");
    p.f("normal", 8, DARK);
    const dLines = p.doc.splitTextToSize(desc, dw - 3);
    const extras = [
        product?.length && product?.width && product?.height
            ? `Dimensions: L${product.length}" x W${product.width}" x H${product.height}"` : null,
        `Flow Rate: ${san(product?.scheduleTypeId_data?.[0]?.title || "N/A")} GPM`,
        product?.manufacturer3Id_data?.[0]?.title && product.manufacturer3Id_data[0].title !== "N/A"
            ? `Grease Capacity: ${san(product.manufacturer3Id_data[0].title)} lbs` : null,
        product?.manufacturer4Id_data?.[0]?.title
            ? `Liquid Capacity: ${san(product.manufacturer4Id_data[0].title)} gal` : null,
    ].filter(Boolean);

    const contentH = dLines.length * 4.2 + extras.length * 5.5 + 10;
    const bh = Math.max(36, contentH);
    p.chk(bh + 6);
    const by = p.y;

    // solid left color #0756A2
    p.fill(M, by, MW, bh, [7, 86, 162]);
    p.f("bold", 7, [200, 220, 255]);
    p.t("MIFAB MODEL", M + MW / 2, by + 8, { align: "center", maxW: MW - 4 });
    const mn = san(product?.images?.[0]?.modelName || "N/A");
    const ml = p.doc.splitTextToSize(mn, MW - 6);
    p.f("bold", ml.length > 1 ? 9 : 11, WHITE);
    ml.forEach((l, i) => { try { p.doc.text(l, M + MW / 2, by + 16 + i * 6, { align: "center" }); } catch (e) { } });

    // right panel
    const dx = M + MW + 4;
    let dy = by + 7;

    // Inline Description
    const descLabel = "Description: ";
    p.f("bold", 8, DARK);
    const dlw = p.doc.getTextWidth(descLabel);
    p.t(descLabel, dx, dy);

    p.f("normal", 8, DARK);
    const firstLineLines = p.doc.splitTextToSize(desc, dw - 3 - dlw);
    const firstLine = firstLineLines[0] || "";
    p.t(firstLine, dx + dlw, dy);
    dy += 4.2;

    const remaining = desc.slice(firstLine.length).trim();
    if (remaining) {
        const otherLines = p.doc.splitTextToSize(remaining, dw - 3);
        otherLines.forEach(l => { p.t(l, dx, dy); dy += 4.2; });
    }

    dy += 2;
    extras.forEach(e => {
        const parts = e.split(": ");
        if (parts.length > 1) {
            p.f("bold", 8.5, DARK); p.t(parts[0] + ": ", dx, dy);
            const lw = p.doc.getTextWidth(parts[0] + ": ");
            p.f("normal", 8.5, DARK); p.t(parts[1], dx + lw, dy);
        } else {
            p.f("normal", 8.5, DARK); p.t(e, dx, dy);
        }
        dy += 5.5;
    });

    // outer border
    p.doc.setDrawColor(7, 86, 162); p.doc.setLineWidth(0.5);
    p.doc.rect(M, by, CW, bh, "S");
    p.y = by + bh; p.gap(6);
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN RENDER
// ─────────────────────────────────────────────────────────────────────────────
async function renderPdf(projectData, productData, secondaryProductData, representativeUser) {
    const { mp, logoUrl } = await gatherImages(productData, secondaryProductData);
    const getSrc = fn => fn ? (mp[`${process.env.REACT_APP_IMAGE_BASEURL}${fn}`] || null) : null;

    const s1 = projectData?.step1 || {}, s2 = projectData?.step2 || {};
    const s3 = projectData?.step3 || {}, s4 = projectData?.step4 || {};
    const addr = [s1.address?.street, s1.address?.city, s1.address?.state, s1.address?.zip].filter(Boolean).join(", ");
    const ctype = s1.constructionType?.replace(/([A-Z])/g, " $1").replace(/^./, c => c.toUpperCase()).trim() || "";
    const date = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

    const logoB64 = mp[logoUrl] || null;
    const mainImg = getSrc(productData?.images?.[0]?.fileName);
    const secImg = getSrc(secondaryProductData?.images?.[0]?.fileName);

    const p = new P();

    // ── HEADER ──────────────────────────────────────────────
    p.fill(M, p.y, CW, 26, BLUE);
    p.f("bold", 17, WHITE); p.t(san(s1.projectName || "Project Name"), M + 4, p.y + 11, { maxW: CW - 26 });
    p.f("normal", 8.5, [210, 224, 245]);
    if (addr) p.t(san(addr), M + 4, p.y + 17, { maxW: CW - 26 });
    if (ctype) p.t(san(ctype), M + 4, p.y + 22, { maxW: CW - 26 });
    if (logoB64) p.addImg(logoB64, M + CW - 22, p.y + 3, 18, 18);
    p.y += 26; p.gap(7);

    // ── RECOMMENDED INTERCEPTOR ─────────────────────────────
    const itype = san(productData?.manufacturerId_data?.[0]?.title?.split(" ")[0] || "");
    p.secH(`Recommended ${itype} Interceptor`);
    drawProductCard(p, productData, mainImg);

    // ── SECONDARY ───────────────────────────────────────────
    if (secondaryProductData) {
        p.secH("Recommended Solids Interceptor");
        drawProductCard(p, secondaryProductData, secImg);
        p.noteBox(
            "In commercial kitchens, grease interceptors can get overwhelmed when too much liquid flows through them too fast. " +
            "To prevent overflow, companies install a solids interceptor before the grease interceptor -- it catches solid food waste first, protecting the grease trap.\n\n" +
            "If the kitchen produces a lot of solid waste along with grease, we need TWO interceptors -- one for solids, one for grease."
        );
    }

    // ── RELATED PRODUCTS ────────────────────────────────────
    const related = productData?.relatedProducts || [];
    if (related.length) {
        p.secH("Related Products");
        drawRelated(p, related, getSrc);
    }

    // ── FIXTURE INFORMATION ─────────────────────────────────
    const fixtures = (s3.fixtures || []).filter(f => f.included);
    if (fixtures.length) {
        p.secH("Fixture Information");
        p.fill(M, p.y, 110, 8, LG);
        p.f("bold", 8.5, DARK);
        p.t(`Pipe size connected to the interceptor: ${san(s3.drainagePipeSize)}"`, M + 3, p.y + 5.5, { maxW: 105 });
        p.y += 8; p.gap(4);

        const stdFix = fixtures.filter(f => f.fixtureType !== "custom_sink");
        const csmFix = fixtures.filter(f => f.fixtureType === "custom_sink");

        if (stdFix.length > 0) {
            p.f("bold", 9, DARK); p.t("Standard Fixtures", M, p.y + 4, { maxW: CW }); p.y += 6;
            const fH = ["Fixture Name", "Trap Size", "DFU Type", "DFU Value", "Flow Rate (GPM)", "Qty"];
            const fW = [54, 22, 28, 24, 36, 18];
            const fR = stdFix.map(f => [san(f.fixtureName), `${san(f.trapSize || "0")}"`, san(f.dfuType || "N/A"), san(f.dfuValue || "0"), san(f.flowRate), san(f.quantity)]);
            p.table(fH, fR, fW);
            p.gap(6);
        }

        if (csmFix.length > 0) {
            p.f("bold", 9, DARK); p.t("Custom Sink Sizing", M, p.y + 4, { maxW: CW }); p.y += 6;
            const cH = ["Fixture Name", "Length (L)", "Width (W)", "Depth (D)", "Compartments (N)", "Flow Rate (GPM)"];
            const cW = [54, 22, 22, 24, 30, 30];
            const cR = csmFix.map(f => [
                "Custom Sink Sizing",
                san(f.length || f.dimensions?.length || "N/A"),
                san(f.width || f.dimensions?.width || "N/A"),
                san(f.depth || f.dimensions?.depth || "N/A"),
                san(f.compartments || f.dimensions?.compartments || "N/A"),
                san(f.flowRate)
            ]);
            p.table(cH, cR, cW);
            p.gap(6);
        }

        const tFlow = fixtures.reduce((s, f) => s + parseFloat(f.flowRate || 0), 0).toFixed(2);
        p.totalRow(["", "", "", "", "Total Project Flow Rate (GPM):", tFlow], [54, 22, 22, 24, 45, 15]);
    }

    // ── SIZING DETAILS ──────────────────────────────────────
    p.pg(); // Always start Sizing Details on a new page
    p.secH("Sizing Details");
    drawSizing(p, s1, s2, addr, ctype);

    // ── REPRESENTATIVE ──────────────────────────────────────
    if (representativeUser) {
        p.secH("Representative Information");
        drawRep(p, representativeUser);
    }

    // ── GREASE OUTPUT ───────────────────────────────────────
    if (s4?.menu) {
        p.secH("Grease Capacity Calculation");
        // All fields matching ProjectResults.jsx greaseOutputSection exactly
        const items = [
            { label: "Restaurant Type", value: san(s4.menu) },
            ...(s4.kitchenAppliances?.length ? [{
                label: "In the Kitchen",
                value: s4.kitchenAppliances.map(a => san(a.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()))).join(", ")
            }] : []),
            ...(s4.servingWare ? [{ label: "Kind of Dishes", value: san(s4.servingWare) }] : []),
            ...(s4.menuValue ? [{ label: "Menu Value", value: san(String(s4.menuValue)) }] : []),
            ...(s4.sqfBuilding ? [{ label: "Sq Ft of Building", value: san(String(s4.sqfBuilding)) }] : []),
            ...(s4.diningAreaPercentage ? [{ label: "Dining Area %", value: san(String(s4.diningAreaPercentage)) + "%" }] : []),
            ...(s4.sqfPerPerson ? [{ label: "SQF/Person", value: san(String(s4.sqfPerPerson)) }] : []),
            ...(s4.turnsPerSeat ? [{ label: "Turns per Seat", value: san(String(s4.turnsPerSeat)) }] : []),
            ...(s4.pumpOutCycle ? [{ label: "Pump Out Cycles", value: san(String(s4.pumpOutCycle)) + " Days" }] : []),
        ];
        p.blueBand(items);
        p.chk(10);
        p.fill(M, p.y, CW, 10, BLUE);
        p.f("bold", 9.5, WHITE); p.t("Grease Production:", M + 4, p.y + 6.5, { maxW: 50 });
        p.f("normal", 9.5, [215, 235, 255]);
        p.t(s4.lbs ? `${parseFloat(s4.lbs).toFixed(2)} lbs` : "N/A", M + 52, p.y + 6.5, { maxW: 60 });
        p.y += 10; p.gap(6);
    }

    // ── CALCULATIONS ────────────────────────────────────────
    if (fixtures.length) {
        p.chk(18);
        p.f("normal", 7.5, GREY);
        p.y += 5;
        p.f("bold", 12, DARK); p.t("CALCULATIONS - ASME SIZING GUIDELINES", M, p.y, { maxW: CW }); p.y += 8;

        // Step 1
        p.f("bold", 9.5, DARK); p.t("Step 1: Flow Rate", M, p.y, { maxW: CW }); p.y += 5;
        p.f("normal", 8, GREY); p.t("(cu in / 231) = gal x 0.75 / 2 min = 2 min flow rate", M, p.y, { maxW: CW }); p.y += 5;

        const cH = ["NAME/TAG", "TYPE", "DIMENSIONS", "QTY", "CU IN", "FLOW RATE (2 MIN)"];
        const cW = [46, 40, 32, 16, 18, 30];
        const cR = fixtures.map(f => {
            const qty = parseInt(f.quantity) || 1;
            const flow = (parseFloat(f.flowRate) || 0);
            const dims = f.fixtureType === "custom_sink"
                ? (f.dimensions?.length && f.dimensions?.width && f.dimensions?.depth
                    ? `${f.dimensions.length} x ${f.dimensions.width} x ${f.dimensions.depth}`
                    : "N/A")
                : (f.length && f.width && f.height
                    ? `${f.length}" x ${f.width}" x ${f.height}"`
                    : "N/A");
            return [
                san(f.fixtureType === "custom_sink" ? "Custom Sink Sizing" : f.fixtureName),
                san(f.fixtureType === "custom_sink" ? "Custom Sink Sizing" : (f.fixtureType === "other" ? "Manual Fixture Sizing" : (f.fixtureType || f.fixtureName))),
                dims,
                qty,
                "N/A",
                `${fmtGPM(flow)} GPM`
            ];
        });
        p.table(cH, cR, cW);
        const raw = fixtures.reduce((s, f) => s + (parseFloat(f.flowRate) || 0), 0);
        p.totalRow(["", "", "", "", "Total", `${fmtGPM(raw)} GPM`], cW);

        // pipe note
        const ps = String(s3.drainagePipeSize || "");
        if (PIPE_MIN[ps]) {
            const gpm = san(productData?.scheduleTypeId_data?.[0]?.title || "N/A");
            p.chk(11);
            const ny = p.y;
            p.fill(M, ny, CW, 10, LBG);
            p.doc.setFillColor(7, 86, 162); p.doc.rect(M, ny, 1.8, 10, "F");
            p.f("bold", 8.5, DARK);
            p.t(`Pipe size (${ps} in) flow rate minimum used to size interceptor: ${gpm} GPM`, M + 5, ny + 6, { maxW: CW - 8 });
            p.y = ny + 10; p.gap(5);
        }

        // Step 2
        if (s4?.menu && s4?.menuValue) {
            p.f("bold", 9.5, DARK); p.t("Step 2: Grease Production", M, p.y, { maxW: CW }); p.y += 5;
            p.f("normal", 8, GREY);
            p.t("Servings per day x Grease production value x Days between pump-outs = Grease output", M, p.y, { maxW: CW }); p.y += 5;

            // Match ProjectResults.jsx exactly: pumpOutCycle and turnsPerSeat from data
            const mv = parseFloat(s4.menuValue) || 0;
            const lb = parseFloat(s4.lbs) || 0;
            const days = s4.pumpOutCycle ? parseInt(s4.pumpOutCycle) : 90;
            const turns = s4.turnsPerSeat ? parseInt(s4.turnsPerSeat) : 1;
            const rate = mv > 0 ? lb / (mv * days) : 0;
            // Match ProjectResults.jsx flatware check (silver OR flatware keyword)
            const fw = (s4.servingWare || "").toLowerCase().includes("silver") ||
                (s4.servingWare || "").toLowerCase().includes("flatware")
                ? "flatware" : "no flatware";
            // Match ProjectResults.jsx greaseLabel: menuType.toLowerCase()/flatware
            const menuType = san(s4.menu || "");
            const greaseLabel = `${rate.toFixed(4)} (${menuType.toLowerCase()}/${fw})`;

            const g2H = ["NAME", "QTY", "TURNS", "MPD", "GREASE PRODUCTION", "PUMPOUT", "TOTAL"];
            const g2W = [32, 14, 16, 16, 54, 22, 28];
            p.table(g2H, [[menuType, "1", `x ${turns}`, mv, greaseLabel, days, `${lb.toFixed(2)} lbs.`]], g2W);
            p.totalRow(["", "", "", "", "", "Total", `${lb.toFixed(2)} lbs`], g2W);
        }

        drawModelBox(p, productData);

        p.chk(12); p.line(M, p.y, M + CW, p.y, LG, 0.3); p.y += 5;
        p.f("normal", 8, GREY);
        p.t("Consult plumbing schedule for solids interceptor and sampling port details.", M, p.y, { maxW: CW });
        p.y += 6;
    }

    // ── FOOTER ──────────────────────────────────────────────
    p.gap(6); p.line(M, p.y, M + CW, p.y, LG, 0.3); p.y += 5;
    p.f("normal", 7.5, GREY);
    p.t(`Generated by MIFAB  -  ${date}`, PW / 2, p.y, { align: "center" });

    return p.doc;
}

// ─────────────────────────────────────────────────────────────────────────────
// EXPORTS
// ─────────────────────────────────────────────────────────────────────────────
export const generateProjectPdf = async (projectData, productData, secondaryProductData = null, representativeUser = null) => {
    const doc = await renderPdf(projectData, productData, secondaryProductData, representativeUser);
    doc.save(`${san(projectData?.step1?.projectName) || "Project"}_Report.pdf`);
};

export const generateProjectPdfBlob = async (projectData, productData, secondaryProductData = null, representativeUser = null) => {
    try {
        const doc = await renderPdf(projectData, productData, secondaryProductData, representativeUser);
        return doc.output("blob");
    } catch (err) {
        console.error("generateProjectPdfBlob error:", err);
        return null;
    }
};

export function buildPdfHtml() { return "<!-- unused -->"; }